import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { redirectByRole } from '../utils/roleRedirect';

// Checked once at module load, before anything can clear the URL hash.
// A return from Microsoft looks like: /#code=...&state=...  (or #error=...)
const isMsalRedirect = (() => {
  const hash = window.location.hash;
  return hash.includes('state=') && /[#&](code|id_token|error)=/.test(hash);
})();

const MicrosoftRedirectHandler = () => {
  const { loginWithMicrosoft } = useAuth();
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (!isMsalRedirect || hasProcessed.current) return;
    hasProcessed.current = true;

    (async () => {
      try {
        const { msalInstance } = await import('../config/msalConfig');
        await msalInstance.initialize();
        const response = await msalInstance.handleRedirectPromise();

        if (response && response.idToken) {
          const result = await loginWithMicrosoft(response.idToken);

          if (result.success && result.data?.next_step === 'SETUP_2FA') {
            navigate('/2fa/setup', { replace: true });
            return;
          }
          if (result.success && result.data?.next_step === 'VERIFY_OTP') {
            navigate('/2fa/verify', { replace: true });
            return;
          }
          if (result.success && result.data?.user) {
            redirectByRole(result.data.user.role, navigate);
          }
        }
      } catch (error) {
        if (error?.errorCode !== 'no_token_request_cache_error') {
          console.error('Microsoft redirect handling error:', error);
        }
      }
    })();
  }, [loginWithMicrosoft, navigate]);

  return null;
};

export default MicrosoftRedirectHandler;